import Layout from '@/components/layout';
import { RememberType } from '@/constants/enum';
import { checkShowErrorInline, handleToastMutation } from '@/helpers';
import i18n from '@/i18n';
import { apiCaller } from '@/redux/query';
import { initialState } from '@/redux/slice/shop.slice';
import { handleToast } from '@/redux/slice/Toast/toast.slice';
import {
  Badge,
  BlockStack,
  Box,
  Button,
  Card,
  Divider,
  InlineGrid,
  InlineStack,
  Select,
  Layout as ShopifyLayout,
  Text,
  TextField,
  useBreakpoints,
} from '@shopify/polaris';
import { isEqual } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

const Settings = () => {
  const dispatch = useDispatch();

  const [updateShopSetting, { isLoading }] = apiCaller.useUpdateAppSettingsMutation();
  const [updateAppStatus, { isLoading: loading }] = apiCaller.useUpdateAppStatusMutation();
  const { data: shopGeneral } = apiCaller.useGetGeneralDetailQuery(undefined);
  const { smUp } = useBreakpoints();
  const { t } = useTranslation(['setting', 'common']);

  const [appSetting, setAppSetting] = useState({
    cacheTime: 10,
    rememberType: RememberType.Days,
  });

  const appGeneralRef = useRef(appSetting);

  const supportLanguages = [
    {
      label: 'English',
      value: 'en',
    },
    {
      label: 'Deutsch',
      value: 'de',
    },
    {
      label: 'Français',
      value: 'fr',
    },
    {
      label: 'Italiano',
      value: 'it',
    },
    {
      label: 'Español',
      value: 'es',
    },
    {
      label: 'हिन्दी (Hindi)',
      value: 'hi',
    },
    // {
    //   label: 'Tiếng việt',
    //   value: 'vi',
    // },
  ];

  const rememberOptions = [
    {
      label: t('session_only'),
      value: RememberType.SessionOnly,
    },
    {
      label: t('day_options'),
      value: RememberType.Days,
    },
    {
      label: t('allow_users_to_choose'),
      value: RememberType.TickBox,
    },
  ];

  const handleChangeAppStatusValue = useCallback(async () => {
    const res = await updateAppStatus(!shopGeneral?.data.appStatus);
    const resToast = handleToastMutation(res, t);
    if ('data' in res) {
      appGeneralRef.current = { ...appSetting };
      dispatch(handleToast({ ...resToast, content: t('text_updated') }));
    } else {
      const isError = checkShowErrorInline(res);
      if (isError.statusCode) {
        dispatch(handleToast(handleToastMutation(isError.message, t)));
      }
    }
  }, [appSetting, dispatch, t, updateAppStatus, shopGeneral]);

  const handleChangeCacheTimeValue = useCallback((value: string) => {
    setAppSetting((pre) => ({ ...pre, cacheTime: Number(value) <= 0 ? 0 : parseInt(value) }));
  }, []);

  const handleChangeRememberOptions = useCallback((value: RememberType) => {
    setAppSetting((pre) => ({ ...pre, rememberType: value }));
  }, []);

  const areSettingEqual = useMemo(() => {
    return isEqual(appSetting, appGeneralRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appSetting, appGeneralRef.current]);

  const handleDiscard = () => {
    setAppSetting({ ...appGeneralRef.current });
  };

  const handleChangeLanguage = useCallback(
    (value: string) => {
      i18n.changeLanguage(value).then(() => {
        dispatch(
          handleToast({
            content: t('text_updated'),
            error: false,
            isOpen: true,
          }),
        );
      });
    },
    [dispatch, t],
  );

  const handleSave = async () => {
    const res = await updateShopSetting({
      cacheTime: appSetting.cacheTime,
      rememberType: appSetting.rememberType,
    });
    const resToast = handleToastMutation(res, t);

    if ('data' in res) {
      appGeneralRef.current = { ...appSetting };
      dispatch(handleToast({ ...resToast, content: t('text_updated') }));
      return;
    } else {
      const isError = checkShowErrorInline(res);
      if (isError.statusCode) {
        dispatch(handleToast(handleToastMutation(isError.message, t)));
      }
    }
  };

  const layoutSettings = {
    primaryAction: {
      content: t('common:save'),
      onAction: handleSave,
      disabled: areSettingEqual,
      loading: isLoading,
    },
    secondaryActions: (
      <div style={{ display: 'flex', gap: '8px' }}>
        <Button disabled={areSettingEqual} onClick={handleDiscard}>
          {t('common:discard')}
        </Button>
      </div>
    ),
  };

  useEffect(() => {
    if (shopGeneral && shopGeneral.data) {
      setAppSetting({
        cacheTime: shopGeneral.data.cacheTime,
        rememberType: shopGeneral.data.rememberType,
      });
      appGeneralRef.current = {
        cacheTime: shopGeneral.data.cacheTime,
        rememberType: shopGeneral.data.rememberType,
      };
    }
  }, [shopGeneral]);

  return (
    <Layout title={t('settings')} {...layoutSettings}>
      <ShopifyLayout>
        <ShopifyLayout.AnnotatedSection id="general" title={t('general')} description={t('general_subheading')}>
          <Card>
            <InlineGrid columns={'1fr auto'}>
              <Box paddingBlockStart={'100'}>
                <InlineStack gap={'150'}>
                  <Text as="h1" variant="headingSm">
                    {t('active_app_name', { appName: 'Age Verification' })}{' '}
                    {shopGeneral?.data.appStatus ? (
                      <Badge size="small" tone="success">
                        {t('common:on')}
                      </Badge>
                    ) : (
                      <Badge size="small" tone="warning">
                        {t('common:off')}
                      </Badge>
                    )}
                  </Text>
                </InlineStack>
              </Box>
              <Button onClick={handleChangeAppStatusValue} loading={loading}>
                {shopGeneral?.data.appStatus ? t('turn_off') : t('turn_on')}
              </Button>
            </InlineGrid>
          </Card>
        </ShopifyLayout.AnnotatedSection>

        {smUp ? <Divider /> : null}

        <ShopifyLayout.AnnotatedSection id="storeDetails" title={t('advanced')} description={t('advanced_subheading')}>
          <BlockStack gap={'300'}>
            <Card>
              <Select
                label={
                  <Text as="h1" variant="headingSm">
                    {t('admin_language')}
                  </Text>
                }
                options={supportLanguages}
                value={i18n.language}
                onChange={handleChangeLanguage}
              />
              <Box paddingBlockStart={'100'}>
                <Text variant="bodyMd" as="span">
                  {t('translated_immediately')}
                </Text>
              </Box>
            </Card>
            <Card>
              {/* <FormLayout> */}
              <Text as="h1" variant="headingSm">
                {t('remember_visitor')}
              </Text>
              <Box paddingBlockStart={'150'}>
                <Select
                  label=""
                  options={rememberOptions}
                  onChange={handleChangeRememberOptions}
                  value={appSetting.rememberType}
                />
              </Box>
              {appSetting.rememberType === RememberType.Days ? (
                <Box paddingBlockStart={'200'}>
                  <TextField
                    autoComplete=""
                    label={
                      <Text variant="headingSm" as="h1">
                        {t('enter_number_of_days')}
                      </Text>
                    }
                    value={appSetting.cacheTime?.toString()}
                    type="number"
                    onChange={handleChangeCacheTimeValue}
                    onBlur={() => {
                      if (appSetting.cacheTime === null || appSetting.cacheTime === undefined)
                        handleChangeCacheTimeValue(Number(initialState.cacheTime).toFixed());
                    }}
                    suffix={t('days') === 'days' ? 'day(s)' : t('days')}
                  />
                </Box>
              ) : null}
            </Card>
          </BlockStack>
        </ShopifyLayout.AnnotatedSection>
      </ShopifyLayout>
    </Layout>
  );
};

export default Settings;
